import config from 'config';
import externalLinks from 'config/externalLinks';
import { trackers } from 'utilities/analytics';

class AnalyticsService {
  constructor() {
    if (AnalyticsService._instance) {
      return AnalyticsService._instance;
    }

    this._trackerNames = Object.keys(trackers);
    this._initialiseTrackers();
    this._facebookPixelTracking = false;

    AnalyticsService._instance = this;
  }

  /* Buttons
  ========================================================================== */
  buttonClickGeneric = (label) => {
    if (config.env !== 'production') {
      console.warn(
        'Are you sure you meant to call `buttonClickGeneric` in ' +
          '`AnalyticsService` and not one of the button click tracking methods?',
      );
    }

    this._trackButtonClick({ label });
  };

  buttonClickCookieBannerConfirm = () => {
    this._trackButtonClick({ label: 'Cookie Banner Confirm' });
  };

  /* Buttons | Navigation
  ========================================================================== */
  buttonClickMenuBack = () => {
    this._trackButtonClick({ label: 'Menu Back' });
  };

  buttonClickMenuLogout = () => {
    this._trackButtonClick({ label: 'Menu Logout' });
  };

  buttonClickMenuMyHugggs = () => {
    this._trackButtonClick({ label: 'Menu My Hugggs' });
  };

  buttonClickFooterTerms = () => {
    this._trackButtonClick({ label: 'Footer Partner Terms' });
  };

  /* Buttons | My Hugggs
  ========================================================================== */
  buttonClickMyHugggsViewHuggg(hugggId) {
    this._trackButtonClick({ label: 'My Hugggs View Huggg' });
    this._trackButtonClick({ label: 'My Hugggs View Huggg: ' + hugggId });
  }

  /* Buttons | Map Dialog
  ========================================================================== */
  buttonClickMapClose = () => {
    this._trackButtonClick({ label: 'Hide Map Button' });
  };

  buttonClickMapOpen = () => {
    this._trackButtonClick({ label: 'Show Map Button' });
  };

  buttonClickMapOpenPlaceholder = () => {
    this._trackButtonClick({ label: 'Show Map Placeholder' });
  };

  /* Buttons | SMS Challenge Form
  ========================================================================== */
  buttonClickSMSChallengeRetry = () => {
    this._trackButtonClick({ label: 'SMS Challenge Resend' });
  };

  buttonClickSMSChallengeWrongNumber = () => {
    this._trackButtonClick({ label: 'SMS Challenge Wrong Number' });
  };

  /* Buttons | Huggg Actions
  ========================================================================== */
  buttonClickHugggActionsMoreInfo = () => {
    this._trackButtonClick({ label: 'Huggg Actions go to More Info' });
  };

  buttonClickHugggActionsStash = () => {
    this._trackButtonClick({ label: 'Huggg Actions go to Stash' });
  };

  buttonClickHugggActionsRedeem = () => {
    this._trackButtonClick({ label: 'Huggg Actions go to Redeem' });
  };

  buttonClickSuperhugggActionsMoreInfo = () => {
    this._trackButtonClick({ label: 'Superhuggg Actions go to More Info' });
  };

  buttonClickSuperhugggActionsStash = () => {
    this._trackButtonClick({ label: 'Superhuggg Actions go to Stash' });
  };

  buttonClickResendPin4 = () => {
    this._trackButtonClick({ label: 'Pin4 Actions resend SMS' });
  };

  /* Dialog Boxes
  ========================================================================== */
  dialogViewedLocationsMap = () => {
    this._trackDialog('Locations Map Dialog', 'locations-map');
  };

  /**
   * If we ever see this event show up in analytics, we got a problem
   */
  dialogViewedSMSChallengePin = () => {
    this._trackDialog('SMS Challenge Pin Dialog', 'sms-challenge-pin');
  };

  dialogViewedWhatIsAValidNumber = () => {
    this._trackDialog('Invalid Number Help Dialog', 'invalid-number-help');
  };

  /* Forms
  ========================================================================== */
  formSubmitSMSChallengePhoneNumber = () => {
    this._trackFormEvent({ label: 'SMS Challenge: Number' });
  };

  formSubmitSMSChallengePin = () => {
    this._trackFormEvent({ label: 'SMS Challenge: Pin' });
  };

  formEventLoginRememberMe = (value) => {
    const label = 'Remember Me Checkbox Changed: ' + JSON.stringify(value);
    this._trackFormEvent({ action: 'click', label });
  };

  /* Outbound Links
  ========================================================================== */
  outboundLinkGeneric = (target) => {
    if (config.env !== 'production') {
      console.warn(
        'Are you sure you meant to call `outboundLinkGeneric` in ' +
          '`AnalyticsService` and not one of the outbound link ' +
          'tracking methods?',
      );
    }

    this._trackOutboundLink(target);
  };

  outboundLinkTwitter = () => {
    this._trackOutboundLink(externalLinks.social.twitter.url);
  };

  outboundLinkEmailHello = () => {
    this._trackOutboundLink(externalLinks.email.hello.url);
  };

  outboundLinkHugggHome = () => {
    this._trackOutboundLink(externalLinks.huggg.home.url);
  };

  outboundLinkHugggTerms = () => {
    this._trackOutboundLink(externalLinks.huggg.terms.url);
  };

  outboundLinkHugggPrivacy = () => {
    this._trackOutboundLink(externalLinks.huggg.privacy.url);
  };

  outboundLinkHugggHelp = () => {
    this._trackOutboundLink(externalLinks.huggg.help.url);
  };

  outboundSupportEmail = () => {
    this._trackOutboundLink(externalLinks.email.support.url);
  };

  outboundLinkAppStore = (target, storeName) => {
    if (['apple', 'googlePlay'].indexOf(storeName) === -1) {
      return;
    }

    this._trackOutboundLink(target);
  };

  /* Passive Actions
  ========================================================================== */
  passiveCompleteHugggStash = (data) => {
    this._validateStashDetails(data, 'completeHugggStash');

    this._trackEvent({
      action: 'passive_action',
      category: 'Stash Flow',
      label: 'Complete Huggg Stash',
      details: { ...data, type: 'huggg' },
    });
  };

  passiveCompleteHugggPinRedeem = (data) => {
    this._validateStashDetails(data, 'completeHugggRedeem');

    this._trackEvent({
      action: 'passive_action',
      category: 'Redeem Flow',
      label: 'Complete Pin Redeem',
      details: { ...data, type: 'pin' },
    });
  };

  passiveCompleteHugggVoucherRedeen = (data) => {
    this._validateStashDetails(data, 'completeHugggRedeem');

    this._trackEvent({
      action: 'passive_action',
      category: 'Redeem Flow',
      label: 'Complete Voucher Redeem',
      details: { ...data, type: 'voucher' },
    });
  };

  passiveCompleteSuperhugggStash = (data) => {
    this._validateStashDetails(data, 'completeSuperhugggStash');

    this._trackEvent({
      action: 'passive_action',
      category: 'Stash Flow',
      label: 'Complete Superhuggg Stash',
      details: { ...data, type: 'superhuggg' },
    });
  };

  /* Screen Views
  ========================================================================== */
  screenViewedLogin = () => {
    this._trackScreenView('Login Screen');
  };

  screenViewedMyHugggs = () => {
    this._trackScreenView('My Hugggs Screen');
  };

  screenViewedError = (errorMessage) => {
    this._trackScreenView('Error Screen: ' + errorMessage);
  };

  screenViewedHugggLanding = () => {
    this._trackScreenView('Huggg Landing Screen');
    this._trackScreenView('Huggg Landing Screen', this._replaceShortcode());
  };

  screenViewedHugggMoreInfo = () => {
    this._trackScreenView('Huggg More Information Screen');
    this._trackScreenView(
      'Huggg More Information Screen',
      this._replaceShortcode(),
    );
  };

  screenViewedHugggUnstashed = () => {
    this._trackScreenView('Unstashed Huggg Screen');
    this._trackScreenView('Unstashed Huggg Screen', this._replaceShortcode());
  };

  screenViewedHugggStashed = () => {
    this._trackScreenView('Stashed Huggg Screen');
    this._trackScreenView('Stashed Huggg Screen', this._replaceShortcode());
  };

  screenViewedHugggRedeem = () => {
    this._trackScreenView('Redeem Huggg Screen');
    this._trackScreenView('Redeem Huggg Screen', this._replaceShortcode());
  };

  screenViewedHugggRedeemed = () => {
    this._trackScreenView('Redeemed Huggg Screen');
    this._trackScreenView('Redeemed Huggg Screen', this._replaceShortcode());
  };

  screenViewedHugggRedeemedExpired = () => {
    this._trackScreenView('Redeemed Expired Huggg Screen');
    this._trackScreenView(
      'Redeemed Expired Huggg Screen',
      this._replaceShortcode(),
    );
  };

  screenViewedHugggHowTo = () => {
    this._trackScreenView('How To Redeem Huggg Screen');
    this._trackScreenView(
      'How To Redeem Huggg Screen',
      this._replaceShortcode(),
    );
  };

  screenViewedHugggMerchantTerms = () => {
    this._trackScreenView('Huggg Merchant Terms Screen');
    this._trackScreenView(
      'Huggg Merchant Terms Screen',
      this._replaceShortcode(),
    );
  };

  screenViewedSuperhugggLanding = () => {
    this._trackScreenView('Superhuggg Landing Screen');
    this._trackScreenView(
      'Superhuggg Landing Screen',
      this._replaceShortcode(),
    );
  };

  screenViewedSuperhugggMoreInfo = () => {
    this._trackScreenView('Superhuggg More Information Screen');
    this._trackScreenView(
      'Superhuggg More Information Screen',
      this._replaceShortcode(),
    );
  };

  screenViewedSuperhugggUnstashed = () => {
    this._trackScreenView('Unstashed SuperHuggg Screen');
    this._trackScreenView(
      'Unstashed SuperHuggg Screen',
      this._replaceShortcode(),
    );
  };

  screenViewedSuperhugggStashed = () => {
    this._trackScreenView('Stashed SuperHuggg Screen');
    this._trackScreenView(
      'Stashed SuperHuggg Screen',
      this._replaceShortcode(),
    );
  };

  screenViewedSuperhugggMerchantTerms = () => {
    this._trackScreenView('Superhuggg Merchant Terms Screen');
    this._trackScreenView(
      'Superhuggg Merchant Terms Screen',
      this._replaceShortcode(),
    );
  };

  initialiseFacebookPixel(pixelId) {
    /*
     * TODO: renable advertising trackers facebookPixel, googleTagManager
     *       after RC work
    if (pixelId && !this._facebookPixelTracking) {
      trackers.facebookPixel.initialise(pixelId);
      this._facebookPixelTracking = true;
    }
    */

    return pixelId;
  }

  /* Private Methods
  ========================================================================== */
  _initialiseTrackers() {
    this._trackerNames.forEach((trackerName) => {
      const tracker = trackers[trackerName];

      tracker.load && tracker.load();
    });
  }

  _getDefaultPathname() {
    return window.location.pathname + window.location.search;
  }

  /**
   * Replace the huggg shortcode in the pathname with the word 'shortcode'.
   *
   * @param {String} pathname
   * @link https://regex101.com/r/1q8Pqg/1
   */
  _replaceShortcode(pathname = this._getDefaultPathname()) {
    const regex = /^\/(?:super\/)?([^/?]+)/;
    const matches = pathname.match(regex);

    return pathname.replace(matches[1], 'shortcode');
  }

  _track(type, ...args) {
    this._trackerNames.forEach((trackerName) => {
      const tracker = trackers[trackerName];

      if (tracker[type]) {
        tracker[type](...args);
      }
    });
  }

  _trackButtonClick(options) {
    this._trackEvent({
      action: 'click',
      category: 'Button',
      ...options,
    });
  }

  _trackDialog(name, pathname) {
    this._track('trackDialog', name, pathname);
  }

  _trackFormEvent(options) {
    this._trackEvent({
      action: 'submit',
      category: 'Form',
      ...options,
    });
  }

  _trackEvent(data) {
    this._track('trackEvent', data);
  }

  _trackOutboundLink(target) {
    this._track('trackOutboundLink', target);
  }

  _trackScreenView(name, pathname = this._getDefaultPathname()) {
    this._track('trackScreen', name, pathname);
  }

  _validateStashDetails(details, name) {
    if (
      !details.hugggId ||
      !details.brandName ||
      !details.brandId ||
      !details.productId ||
      !details.productName
    ) {
      console.warn('Logged `' + name + '` event with incomplete data');
    }
  }
}

export default AnalyticsService;
